import { createAvToDownstreamHandoffNotifications } from './createAvToDownstreamHandoffNotifications';
import { parseNumeric } from '../../../shared/utils';
import { isAugsburgByWerkValue } from './plantScope';

type Option = { label: string; value: number };

const NEED_FIELDS = ['inspection_planning_need', 'ncpneed', 'cmmneed', 'qsfaneed'] as const;
type NeedField = (typeof NEED_FIELDS)[number];

export type ProcessAvToDownstreamHandoffNotificationsParams = {
  editedRow: Array<Record<string, any>>;
  editedRowOriginal: Record<string, Record<string, any>>;
  allAccessibleColumns: any[];
  hasAvRole: boolean;
  screenGridId: number;
  createdBy: number;
  configSchema: string;
};
function hasRowId(row: Record<string, any>): row is { id1: number | string } & Record<string, any> {
  return typeof row?.id1 === 'number' || typeof row?.id1 === 'string';
}

function resolveYesNeedId(valueOptionsNeed: Option[]): number | null {
  const yesOption = valueOptionsNeed.find((option) => option.label?.toLowerCase() === 'yes');
  return yesOption?.value ?? null;
}

function resolveInspectionPlanningTriggerIds(valueOptionsNeed: Option[]): Set<number> {
  const triggerLabels = new Set(['full fai', 'delta fai']);
  return new Set(
    valueOptionsNeed
      .filter((option) => triggerLabels.has(option.label?.trim().toLowerCase() ?? ''))
      .map((option) => option.value),
  );
}

function isNeedSelected(
  field: NeedField,
  value: unknown,
  yesNeedId: number | null,
  inspectionPlanningTriggerIds: Set<number>,
): boolean {
  const parsedValue = parseNumeric(value);
  if (parsedValue == null) {
    return false;
  }

  if (field === 'inspection_planning_need') {
    return inspectionPlanningTriggerIds.has(parsedValue);
  }

  return yesNeedId != null && parsedValue === yesNeedId;
}

function getRowsWithNeedFieldChanges({
  editedRows,
  editedRowsOriginal,
  yesNeedId,
  inspectionPlanningTriggerIds,
}: {
  editedRows: Array<Record<string, any>>;
  editedRowsOriginal: Record<string, Record<string, any>>;
  yesNeedId: number | null;
  inspectionPlanningTriggerIds: Set<number>;
}): Array<{
  row: { id1: number | string } & Record<string, any>;
  needFields: NeedField[];
}> {
  return (editedRows || []).flatMap((row) => {
    if (!hasRowId(row)) {
      return [];
    }

    const rowId = row.id1;

    const originalRow = editedRowsOriginal?.[rowId];
    if (!originalRow) {
      return [];
    }

    const transitionedNeedFields = NEED_FIELDS.filter((field) => {
      const currentSelected = isNeedSelected(
        field,
        row?.[field],
        yesNeedId,
        inspectionPlanningTriggerIds,
      );
      const previousSelected = isNeedSelected(
        field,
        originalRow?.[field],
        yesNeedId,
        inspectionPlanningTriggerIds,
      );

      return currentSelected && !previousSelected;
    });

    return transitionedNeedFields.length ? [{ row, needFields: transitionedNeedFields }] : [];
  });
}

/**
 * Creates AV -> downstream handoff notifications for need fields that transitioned to selected.
 */
export async function processAvToDownstreamHandoffNotifications({
  editedRow,
  editedRowOriginal,
  allAccessibleColumns,
  hasAvRole,
  screenGridId,
  createdBy,
  configSchema,
}: ProcessAvToDownstreamHandoffNotificationsParams): Promise<string | null> {
  if (!editedRow.length || !hasAvRole) {
    return null;
  }

  const valueOptionsProfitCenter = (allAccessibleColumns.find(
    (col) => col.attrInternalName === 'pf_lc',
  )?.valueOptions ?? []) as Option[];

  const valueOptionsNeed = (allAccessibleColumns.find(
    (col) => col.attrInternalName === 'inspection_planning_need',
  )?.valueOptions ?? []) as Option[];
  const valueOptionsWerk = (allAccessibleColumns.find(
    (col) => col.attrInternalName === 'werk',
  )?.valueOptions ?? []) as Option[];

  const yesNeedId = resolveYesNeedId(valueOptionsNeed);
  const inspectionPlanningTriggerIds = resolveInspectionPlanningTriggerIds(valueOptionsNeed);

  const transitionedRows = getRowsWithNeedFieldChanges({
    editedRows: editedRow,
    editedRowsOriginal: editedRowOriginal,
    yesNeedId,
    inspectionPlanningTriggerIds,
  }).filter(({ row }) => isAugsburgByWerkValue(row?.werk, valueOptionsWerk));

  if (!transitionedRows.length) {
    return null;
  }

  try {
    await Promise.all(
      transitionedRows.map(({ row, needFields }) =>
        createAvToDownstreamHandoffNotifications({
          row,
          screenGridId,
          createdBy,
          valueOptionsProfitCenter,
          valueOptionsNeed,
          configSchema,
          needFields,
        }),
      ),
    );

    return null;
  } catch (error) {
    return error instanceof Error ? error.message : 'AV handoff notification creation failed';
  }
}
